/**
 * postMessage 기반 iframe ↔ 플랫폼 통신 모듈
 *
 * 게임(iframe)과 MetaArena 플랫폼(부모 창) 사이의 메시지 교환을 담당합니다.
 * SDK 내부에서만 사용합니다.
 */

import type { OutboundMessage, InboundMessage } from './types.js'

type InboundType = InboundMessage['type']
type MessageHandler<T = unknown> = (data: T) => void

export class Messenger {
  private handlers = new Map<string, MessageHandler[]>()
  private boundListener: (event: MessageEvent) => void

  constructor() {
    this.boundListener = this.handleMessage.bind(this)
    window.addEventListener('message', this.boundListener)
  }

  /** 수신 메시지 핸들러 등록 */
  on<T extends InboundMessage>(type: T['type'], handler: MessageHandler<T>): void {
    const existing = this.handlers.get(type) ?? []
    this.handlers.set(type, [...existing, handler as MessageHandler])
  }

  /** 수신 메시지 핸들러 해제 */
  off<T extends InboundMessage>(type: T['type'], handler: MessageHandler<T>): void {
    const existing = this.handlers.get(type) ?? []
    this.handlers.set(type, existing.filter(h => h !== handler))
  }

  /** 부모 창(플랫폼)으로 메시지 전송 */
  sendToHost(message: OutboundMessage): void {
    if (typeof window === 'undefined' || window.parent === window) return
    window.parent.postMessage(message, '*')
  }

  private handleMessage(event: MessageEvent): void {
    const data = event.data as InboundMessage | null
    if (!data?.type?.startsWith('METAARENA_')) return

    const handlers = this.handlers.get(data.type) ?? []
    handlers.forEach(handler => handler(data))
  }

  /** 이벤트 리스너 해제 */
  destroy(): void {
    window.removeEventListener('message', this.boundListener)
    this.handlers.clear()
  }
}

/**
 * 플랫폼(호스트) 측 헬퍼 — 게임 iframe으로 GAME_START를 전송할 때 사용
 * (MetaArena 플랫폼 코드에서 사용, 게임 개발자가 직접 사용할 일 없음)
 */
export class HostMessenger {
  sendGameStart(
    frame: HTMLIFrameElement,
    payload: { tournament_id: string; player_token: string; api_base: string },
  ): void {
    const origin = frame.src ? new URL(frame.src).origin : '*'
    frame.contentWindow?.postMessage({ type: 'METAARENA_GAME_START', ...payload }, origin)
  }
}
